/**
 * Computes SHA-256 hash of the input string (lowercased).
 * Returns empty string if crypto API unavailable or error occurs.
 * @param {string} input
 * @returns {Promise<string>}
 */
export async function computeSHA256(input) {
    if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) {
        return ''
    }
    try {
        const encoder = new TextEncoder()
        const data = encoder.encode(input.toLowerCase())
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    } catch {
        return ''
    }
}

function readCookie(name) {
    if (typeof document === 'undefined' || !document.cookie) return null
    const cookies = document.cookie.split(';')
    for (let index = 0; index < cookies.length; index += 1) {
        const separator = cookies[index].indexOf('=')
        if (separator === -1) continue
        if (cookies[index].slice(0, separator).trim() === name) {
            return cookies[index].slice(separator + 1)
        }
    }
    return null
}

/**
 * Returns the GA4 client_id by stripping the GA1.X. prefix from the _ga cookie.
 * Returns null if the cookie is absent (e.g. cookies blocked).
 * @returns {string|null}
 */
export function getGA4ClientId() {
    const value = readCookie('_ga')
    if (!value) return null
    const match = value.match(/^GA\d+\.\d+\.(.+)$/)
    return match ? match[1] : null
}

/**
 * Returns the GA4 session_id from the _ga_MEASUREMENTID cookie.
 * Handles both cookie formats:
 *   Old: GS1.1.1717000000.3.1... → session_id is the plain number at index 2
 *   New: GS2.1.s1774620891$o81$... → session_id is the number after leading "s" at index 2
 * @param {string} measurementId - GA4 Measurement ID without the "G-" prefix (e.g. "4P4KZTEKKQ")
 * @returns {string|null}
 */
export function getGA4SessionId(measurementId) {
    if (!measurementId) return null
    const value = readCookie('_ga_' + String(measurementId).replace(/^G-/, ''))
    if (!value) return null
    const segment = value.split('.')[2] || null
    if (!segment) return null
    // New format: segment starts with "s" followed by the session timestamp (e.g. "s1774620891$o81$...")
    const newFormat = segment.match(/^s(\d+)/)
    if (newFormat) return newFormat[1]
    // Old format: segment is already a plain number
    return segment
}

/**
 * Pushes an analytics event to window.dataLayer with common user info and event-specific name.
 * @param {Object} params
 * @param {string} params.eventName - The event name to push
 * @param {string} params.userId - User ID
 * @param {string} params.email - User email
 * @param {boolean} params.registered - Is user registered
 * @param {string} params.phone - User phone number
 * @param {Object} [params.extraData] - Additional event-specific data to merge
 */
export async function pushAnalyticsEvent({
    eventName,
    userId,
    email,
    registered,
    phone,
    extraData = {}
}) {
    if (typeof window === 'undefined' || !window.dataLayer) return

    const sha256Email = email ? await computeSHA256(email) : ''

    const payload = {
        event: eventName,
        userId: userId || undefined,
        sha256: sha256Email || undefined,
        registered,
        phone,
        event_id: `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...extraData
    }

    if (sha256Email) {
        payload.user_id = sha256Email
    } else {
        payload.user_id = undefined
    }

    window.dataLayer.push(payload)
}
